import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import useDiscountStore from '@/context/DiscountStore'
import { validateDiscountCode, resolveAffiliateLinkDiscountCode, DiscountValidationResponse } from '@/services/discountApi'

const getValidDiscountCaptureKey = (discountCode: string) =>
    `offer-valid-discount-captured:${discountCode.trim().toLowerCase()}`
const inFlightValidations = new Set<string>()

export const useDiscountCapture = () => {
    const location = useLocation()
    const { discountCode, setDiscountCode, setDiscountValidation } = useDiscountStore()
    const lastHandledCaptureKeyRef = useRef<string>('')

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const urlDiscountCode =
            searchParams.get('code') ||
            searchParams.get('coupon') ||
            searchParams.get('discountCode') ||
            searchParams.get('discount')
        const urlAffiliateCode =
            searchParams.get('aff') ||
            searchParams.get('affiliate') ||
            searchParams.get('affiliate_code') ||
            searchParams.get('ref')

        const normalizedCode = (urlDiscountCode || urlAffiliateCode)?.trim()
        const isAffiliateLinkCapture = !urlDiscountCode && Boolean(urlAffiliateCode)
        if (normalizedCode) {
            const normalizedCodeLower = normalizedCode.toLowerCase()
            const captureKey = `${isAffiliateLinkCapture ? 'affiliate' : 'discount'}:${normalizedCodeLower}:${location.search}`
            if (lastHandledCaptureKeyRef.current === captureKey) {
                return
            }
            const currentCode = discountCode?.code?.trim().toLowerCase() || ''
            const isSameCode = currentCode === normalizedCodeLower
            const shouldStartLoading = !discountCode || !isSameCode
            const shouldValidate =
                !discountCode ||
                !isSameCode ||
                (!discountCode.valid && !discountCode.isLoading)

            if (shouldStartLoading) {
                setDiscountCode(normalizedCode)
            }
            if (!shouldValidate) {
                return
            }
            // Already validated this code in store; avoid repeated revalidation loops.
            if (isSameCode && !discountCode?.isLoading && discountCode?.valid) {
                return
            }
            // Avoid duplicate requests when this hook exists in multiple mounted components.
            if (inFlightValidations.has(normalizedCodeLower)) {
                return
            }
            inFlightValidations.add(normalizedCodeLower)
            lastHandledCaptureKeyRef.current = captureKey

            const validate = isAffiliateLinkCapture
                ? resolveAffiliateLinkDiscountCode(normalizedCode)
                : validateDiscountCode(normalizedCode)

            validate
                .then((result: DiscountValidationResponse | null) => {
                    if (!result) {
                        setDiscountValidation({
                            valid: false,
                            error: 'Invalid discount code'
                        })
                        return
                    }
                    setDiscountValidation({
                        code: result.code || normalizedCode,
                        emailBound: result.emailBound,
                        valid: result.valid,
                        discountType: result.discountType,
                        discountValue: result.discountValue,
                        error: result.error || null,
                        freeAddonIds: result.freeAddonIds,
                        freeAddonTypes: result.freeAddonTypes
                    })
                    if (result.valid) {
                        localStorage.setItem(getValidDiscountCaptureKey(normalizedCode), '1')
                    }
                })
                .catch(() => {
                    setDiscountValidation({
                        valid: false,
                        error: 'Failed to validate discount code'
                    })
                })
                .finally(() => {
                    inFlightValidations.delete(normalizedCodeLower)
                })
        }
        // Note: We don't clear discount when no code in URL anymore
        // This allows the persisted discount to remain active
    }, [location.search, discountCode, setDiscountCode, setDiscountValidation])
}

export default useDiscountCapture 
